<template>
  <div>
    <el-tabs v-model="menu">
      <el-tab-pane name="agreement">
        <template #label><span><i class="fa fa-file-signature text-muted"></i> Sharer Agreement</span></template>

        <el-row>
          <el-col :xs="24" :sm="12" :md="6">
            Agreement Start <strong>{{formatDate(details.started)}}</strong>
          </el-col>

          <el-col :xs="24" :sm="12" :md="6">
            Agreement Expiry <strong>{{formatDate(details.expiry)}}</strong>
          </el-col>

          <el-col :xs="24" :sm="12" :md="6">
            Agreement Term <strong v-if="term !== null">{{term}} months</strong><strong v-else>—</strong>
          </el-col>

          <el-col :xs="24" :sm="12" :md="6">
            Expires <strong>{{expiry !== null ? expiry : '—'}}</strong>
          </el-col>
        </el-row>

        <p>
            <h3>Site Financials</h3>
            <el-button v-if="isManager('finance|project') && !readOnly" type="text" aria-label="Edit sharer agreement" @click="editState()"><i :class="editing ? 'fa fa-check-circle text-success' : 'fa fa-edit'"></i></el-button>
            <el-button v-if="this.editing" type="text" @click="editing = false"><i class="fa fa-times-circle text-danger"></i></el-button>
        </p>

        <el-row>
          <el-col :span="24">
            <el-form inline label-position="top" ref='financeForm' :model='details'>

              <el-row :gutter="12">
                <el-col :span="24">
                  <select-sharer-status v-model="details.status" :disabled="!editing" />

                  <date-input v-if="details.started || editing" label="Started" v-model="details.started" :disabled="!editing" />
                  <date-input v-if="details.status !== 'Terminated' && (details.startBilling || editing)" label="Billing from" v-model="details.startBilling" :disabled="!editing" />
                  <date-input v-if="details.status == 'Terminated' && (details.terminated || editing)" label="Terminated" v-model="details.terminated" :disabled="!editing" />
                  <date-input v-if="details.expiry || editing" label="Expiry" v-model="details.expiry" :disabled="!editing" />

                </el-col>
              </el-row>

              <!-- Sharer Rate on its own row + Duplicate & modify (XOL-241) -->
              <!-- XOL-355: hidden when the rate is manually overridden — the tiered
                   sharer rate is irrelevant while a flat override is in effect. -->
              <el-row v-if="!details.overrideEnabled" :gutter="12">
                <el-col :span="24">
                  <sharer-rate-override v-model="details.rate_id" :sharer-id="sharer.organisation_id" :disabled="!editing" @input="onRateChanged" />
                </el-col>
              </el-row>

              <!-- XOL-306: in-form version select (edit mode, version-managers only) -->
              <el-row v-if="editing && details.rate_id && canSwitchVersion && !details.overrideEnabled" :gutter="12">
                <el-col :sm="24" :md="16">
                  <el-form-item label="Rate version">
                    <select-rate-versions
                      :versions="rateVersions"
                      :value="selectedVersionId"
                      :loading="loadingVersions"
                      :disabled="!canSwitchVersion"
                      clearable
                      placeholder="Latest (unversioned)"
                      @input="onSelectVersion" />
                  </el-form-item>
                </el-col>
              </el-row>

              <!-- XOL-259 Phase 2 / XOL-306: read-only version summary (view mode) -->
              <!-- XOL-355: hidden when the rate is manually overridden. -->
              <el-row v-if="!details.overrideEnabled" :gutter="12">
                <el-col :span="24">
                  <el-divider content-position="left">Rate version</el-divider>
                  <sharer-rate-version v-show="!editing" ref="sharerRateVersion" :id="sharer.id" :rate-id="details.rate_id" />
                </el-col>
              </el-row>

              <!-- Transient note after a rate or version change — XOL-306 -->
              <el-alert
                v-if="resumingFee"
                type="info"
                :closable="false"
                show-icon
                title=""
                description="Fees are recalculating in the background — this may take a moment." />

              <el-alert v-if="details && editing" type="warning" title="Sharer specific!" description="Only input values PER sharer. For site wide CAPEX and OPEX see Finance Summary." show-icon  :closable="false" />
                <!-- XOL-319: vertical gap between the Rate version table and the Calculated (auto) rate -->
                <el-row :gutter="12" style="margin-top: 16px;">
                    <el-col :span="24">

                      <!-- XOL-268 Phase B: Current Fees + override controls -->
                      <currency-input
                        v-if="details.overrideEnabled"
                        label="Current Fees"
                        v-model="details.currentRate"
                        :disabled="!editing || !canOverride"
                        placeholder="Enter manual fee" />

                      <!-- Override checkbox — only visible when editing -->
                      <el-tooltip
                        v-if="editing"
                        content="Only finance users can override the rate."
                        placement="bottom"
                        :disabled="canOverride">
                        <check-box
                          v-if="editing"
                          class="override-lock-label"
                          :label="'Override/Lock\nRate'"
                          v-model="details.overrideEnabled"
                          :disabled="!canOverride" />
                      </el-tooltip>

                      <!-- Non-manager helper text (guaranteed visible fallback) -->
                      <p v-if="editing && !canOverride" class="text-muted" style="font-size:12px;margin:0 0 8px;">
                        Only finance users can override the rate.
                      </p>

                      <!-- Calculated (auto) rate display — XOL-268 -->
                      <el-form-item v-if="!details.overrideEnabled" label="Calculated (auto) rate">
                        <span v-if="details.calculatedRate !== null && details.calculatedRate !== undefined">{{calcRateDisplay}}</span>
                        <span v-else>
                          <span>—</span>
                          <span class="text-muted" style="font-size:12px;margin-left:6px;">No calculated rate yet (no rate set, no handover, or past the last tier).</span>
                        </span>
                      </el-form-item>

                      <!-- Transient note after clearing override — XOL-268 -->
                      <el-alert
                        v-if="resumingAuto"
                        type="info"
                        :closable="false"
                        show-icon
                        title=""
                        description="Resuming the calculated rate — this may take a moment to update." />

                      <float-input
                        v-if="details.discount || editing"
                        v-model="details.discount"
                        label="Discount" :prefix="details.discountPercentage ? currency : ''"
                        :suffix="details.discountPercentage ? '' : '%'"
                        :disabled="!editing" />

                        <check-box
                          v-if="editing || details.discount"
                          label="Disc. Type"
                          v-model="details.discountPercentage"
                          :disabled="!editing">
                          {{!details.discountPercentage ? '%' : currency}}
                        </check-box>

                        <float-input
                            v-if="details.interestRate || editing"
                            v-model="details.interestRate"
                            label="Interest Rate"
                            suffix="%"
                            :disabled="!editing" />

                      <float-input v-if="details.capex || editing" label="Sharer Specific CAPEX" v-model="details.capex" :decimals="2" :disabled="!editing" />
                      <float-input v-if="details.opex || editing" label="Sharer Specific OPEX" v-model="details.opex" :decimals="2" :disabled="!editing"  />
                </el-col>

              </el-row>

              <div v-if="details.updated_at">
                <small class="text-muted pull-right">Last update: <b>{{formatFriendlyDate(details.updated_at)}}</b><span v-if="details.updated_by"> by {{details.updated_by}}</span></small>
              </div>

          </el-form>

        </el-col>
      </el-row>

      <el-row v-if="canAccess('finance') && sharer.site_id && sharer.id">
        <el-col :span="24">
          <files-panel title="License Agreement" type="site" :id="sharer.site_id" process="sharer" :process-id="sharer.id" hide-carousel />
        </el-col>
      </el-row>

   </el-tab-pane>


   <el-tab-pane name="sharerInvoice" lazy>
     <template #label><span><i class="fa fa-money-check text-muted"></i> Sharer Invoice</span></template>

     <site-invoice :site-data="siteData" :sharer-id="sharer.organisation_id" :org="sharer" />
   </el-tab-pane>

   <el-tab-pane name="sharerVO" lazy>
     <template #label><span><i class="fa fa-screwdriver text-muted"></i> Sharer V.O.</span></template>

     <site-invoice :site-data="siteData" :sharer-id="sharer.organisation_id" :org="sharer" is-vo />
   </el-tab-pane>


   <el-tab-pane name="billableEquip" lazy>
     <template #label><span><i class="fa fa-tools text-muted"></i> Billable Equip.</span></template>
     <billable-equip-table :site-id="details.site_id" :site-data="siteData" :owner-id="sharer.organisation_id" />
   </el-tab-pane>

   <el-tab-pane name="feeSchedule" lazy>
     <template #label><span><i class="fa fa-calendar-alt text-muted"></i> Fee Schedule</span></template>
     <sharer-fee-schedule :id="sharer.id" :started="details.started" />
   </el-tab-pane>

   <el-tab-pane name="feeTimeline" lazy>
     <template #label><span><i class="fa fa-stream text-muted"></i> Fee Timeline</span></template>
     <sharer-fee-timeline :id="sharer.id" :started="details.started" />
   </el-tab-pane>

 </el-tabs>
  </div>
</template>


<script>

import axios from '@/lib/axiosClient'
import PostMixins from '@/mixins/PostMixins'
import NumberMixins from '@/mixins/NumberMixins'
import DateMixins from '@/mixins/DateMixins'
import {toAppTime, appNow} from '@/lib/appTime'


import {settings} from '@/components/Data'

import SelectSharerStatus from '@/components/atoms/selects/SelectSharerStatus'
import SelectRateVersions from '@/components/atoms/selects/SelectRateVersions'
import SharerRateOverride from '@/components/finance/rates/SharerRateOverride'

import BillableEquipTable from '@/components/assets/subcat/BillableEquipTable'

import SiteInvoice from '@/components/sites/finance/SiteInvoice'
import SharerFeeSchedule from './SharerFeeSchedule'
import SharerFeeTimeline from './SharerFeeTimeline'
import SharerRateVersion from './SharerRateVersion'

export default {
  mixins: [DateMixins, NumberMixins, PostMixins],

  props: {
    sharer: {
      type: Object,
      required: true
    },

    editMode: {
      type: Boolean,
      default: false
    },

    siteData: {
      type: Object
    },

    readOnly: {
      type: Boolean,
      default: false
    }
  },

  components: {
    BillableEquipTable,
    SelectSharerStatus,
    SelectRateVersions,
    SiteInvoice,
    SharerRateOverride,
    SharerFeeSchedule,
    SharerFeeTimeline,
    SharerRateVersion,
  },

  computed: {
    // Both are rendered unconditionally in the header, and `expiry` is
    // genuinely optional (line 42 gates its own input on `details.expiry ||
    // editing`). toAppTime returns null where the old moment(null) returned an
    // *invalid* moment — which absorbed .diff()/.fromNow() as NaN/'Invalid
    // date' — so these must guard or the whole tab throws.
    term: function () {
      var oExpiry = toAppTime(this.details.expiry)
      var oStarted = toAppTime(this.details.started)
      return (oExpiry === null || oStarted === null) ? null : oExpiry.diff(oStarted, 'months')
    },

    expiry: function () {
      var oExpiry = toAppTime(this.details.expiry)
      return (oExpiry === null) ? null : oExpiry.from(appNow())
    },

    // XOL-371 review 2026-07-21: reverted to a manager gate — only finance|project
    // managers may set an override (not read-only). Matches the edit-toggle gate.
    canOverride: function () {
      return this.isManager('finance|project') && !this.readOnly
    },

    // XOL-259 Phase 2 / XOL-371 review 2026-07-21: gates the version switcher —
    // reverted to a finance|project manager gate (not read-only), same as canOverride.
    canSwitchVersion: function () {
      return this.isManager('finance|project') && !this.readOnly
    },

    // XOL-268: formatted calculated rate, dash when null/undefined
    calcRateDisplay: function () {
      var v = this.details.calculatedRate
      if (v === null || v === undefined) {
        return '—'
      }
      return this.formatMoney(v, 2)
    }
  },

  methods: {
    // XOL-268: derive overrideEnabled — prefer the explicit field, fall back to rateLocked
    deriveOverride: function (obj) {
      if (obj && obj.overrideEnabled !== undefined && obj.overrideEnabled !== null) {
        return !!obj.overrideEnabled
      }
      return !!(obj && obj.rateLocked)
    },

    // XOL-268: fold response from PUT /rate-override into local state + reset baselines
    applyOverrideResponse: function (data) {
      if (!data) return
      if (data.currentRate !== undefined) {
        this.details.currentRate = data.currentRate
      }
      if (data.calculatedRate !== undefined) {
        this.details.calculatedRate = data.calculatedRate
      }
      var derived = this.deriveOverride(data)
      this.details.overrideEnabled = derived
      this.originalRate = data.currentRate
      this.originalOverride = derived
    },

    // XOL-268: PUT /licensees/{id}/rate-override
    saveRateOverride: function () {
      var self = this

      // Guard: must have a valid positive rate when override is enabled
      var rate = Number(this.details.currentRate)
      if (!this.details.currentRate || isNaN(rate) || rate <= 0) {
        this.$message({
          type: 'warning',
          title: 'Missing fee',
          message: 'Enter a Current Fee amount before overriding the calculated rate.'
        })
        this.editing = true
        return false
      }

      var path = '/licensees/' + this.details.id + '/rate-override'
      var url = this.getEndpoint(path)

      this.send(
        self,
        url,
        { currentRate: rate },
        'Rate override',
        function (data) {
          self.applyOverrideResponse(data)
        },
        null,
        axios.put,
        true,
        'Save'
      )
      return true
    },

    // XOL-268: DELETE /licensees/{id}/rate-override
    clearRateOverride: function () {
      var self = this

      var path = '/licensees/' + this.details.id + '/rate-override'
      var url = this.getEndpoint(path)

      this.send(
        self,
        url,
        null,
        'Rate override',
        function (data) {
          self.details.overrideEnabled = false
          self.originalOverride = false
          self.resumingAuto = true
          // Do NOT copy stale data.currentRate — let calculatedRate govern display
          if (data && data.calculatedRate !== undefined) {
            self.details.calculatedRate = data.calculatedRate
          }
        },
        null,
        axios.delete,
        true,
        'Save'
      )
    },

    // XOL-306: load published versions for a rate; autoLatest=true pins selectedVersionId
    // to the current version automatically (on rate change), autoLatest=false seeds
    // the existing pin from originalVersionId (on edit-open).
    loadVersionsForRate: function (rateId, autoLatest) {
      var self = this
      // Only version-managers manage the pin; the /rates/{id}/versions endpoint is
      // gated finance|admin|management, so skip the fetch for finance|project-only
      // editors (avoids a 403 + spurious error toast). Their save omits
      // rate_version_id (see commitFinance) so the backend auto-pins on rate change.
      if (!this.canSwitchVersion) {
        this.rateVersions = []
        this.selectedVersionId = this.originalVersionId
        return
      }
      if (!rateId) {
        this.rateVersions = []
        this.selectedVersionId = null
        return
      }
      this.loadingVersions = true
      this.fetchData(
        'rates/' + rateId + '/versions',
        function (data) {
          self.rateVersions = Array.isArray(data) ? data : []
          if (autoLatest) {
            var current = self.rateVersions.find(function (v) { return v.is_current })
            self.selectedVersionId = current ? current.id : null
          } else {
            self.selectedVersionId = self.originalVersionId
          }
          self.loadingVersions = false
        },
        'rate versions',
        function (_err) {
          self.rateVersions = []
          self.selectedVersionId = autoLatest ? null : self.originalVersionId
          self.loadingVersions = false
        }
      )
    },

    // XOL-306: called by @input on sharer-rate-override; auto-defaults version to latest
    onRateChanged: function (rateId) {
      this.loadVersionsForRate(rateId, true)
    },

    // XOL-306: called by @input on select-rate-versions in edit mode
    onSelectVersion: function (id) {
      this.selectedVersionId = (id === undefined || id === null) ? null : id
    },

    // XOL-268 Phase B: extracted write sequence — receives already-computed booleans
    commitFinance: function (overrideChanged, rateChanged, rateIdChanged, versionPinNeeded) {
      if (this.details.overrideEnabled && (overrideChanged || rateChanged)) {
        if (this.saveRateOverride() === false) return  // empty-fee guard: keep editing, no bulk save
      } else if (!this.details.overrideEnabled && overrideChanged) {
        this.clearRateOverride()
      }
      var self = this
      var payload = this.clone(this.details)
      delete payload.currentRate
      delete payload.rateLocked
      // XOL-416: this payload is a blocklist off the full decorateSharer body, so it
      // echoed the sharer's billing slot back like the sharer tabs did. The slot is
      // not editable on this screen, so it is not sent.
      //
      // This is a real guard, not tidiness: it is what stops a RATE edit from moving
      // a sharer's BILLING POSITION as a side effect. Deleting the line would let any
      // stale `sharerNo` in `details` ride along on a save that has nothing to do
      // with it.
      //
      // (XOL-431 removed the neighbouring `delete payload.billing_no`. That column no
      // longer exists, so it stripped a key that was never in the body.)
      delete payload.sharerNo
      // XOL-306/XOL-312: the bulk PUT auto-pins ONLY when rate_id changes — send the
      // version-manager's chosen pin (id, or null = unpin) on a rate change so it's
      // atomic + honoured. A same-rate version switch is ignored by the bulk PUT, so
      // it's persisted via the dedicated re-pin endpoint in the success callback.
      // finance|project-only editors omit the field entirely (backend auto-pins).
      delete payload.rate_version_id
      if (this.canSwitchVersion && rateIdChanged) {
        payload.rate_version_id = this.selectedVersionId
      }
      this.saveForm(this, '/licensees', this.details.id, payload, 'Sharer Financial Data', function (resp) {
        if (rateIdChanged) {
          self.originalRateId = self.details.rate_id
        }
        // Same-rate version change → the bulk PUT ignores rate_version_id when the
        // rate is unchanged (XOL-312), so persist it via the dedicated re-pin
        // endpoint (XOL-259). (Same-rate unpin to null isn't persistable — the
        // endpoint requires an id — so that edge is skipped.)
        if (self.canSwitchVersion && !rateIdChanged && versionPinNeeded && self.selectedVersionId !== null) {
          self.repinVersion(self.selectedVersionId)
        } else {
          self.originalVersionId = self.selectedVersionId
          if (resp) self.onVersionRepinned(resp)
          // Reload the read-only summary after a rate change (its pin/rate moved);
          // a no-op save needs no refresh.
          if (rateIdChanged && self.$refs.sharerRateVersion) self.$refs.sharerRateVersion.load()
        }
        // Transient fee-recalc note when rate or version changed (async recompute)
        if (rateIdChanged || versionPinNeeded) {
          self.resumingFee = true
        }
      })
      this.editing = false
    },

    // XOL-306: persist a SAME-RATE version switch via the dedicated re-pin endpoint
    // (the bulk PUT only auto-pins on a rate change — XOL-312). Mirrors the former
    // SharerRateVersion.doSwitch; folds the decorated licensee + refreshes the summary.
    repinVersion: function (versionId) {
      var self = this
      this.send(
        this,
        this.getEndpoint('/licensees/' + this.details.id + '/rate-version'),
        { rate_version_id: versionId },
        'Rate version',
        function (licensee) {
          self.originalVersionId = versionId
          if (licensee) self.onVersionRepinned(licensee)
          if (self.$refs.sharerRateVersion) self.$refs.sharerRateVersion.load()
        },
        function (_err) {
          // error toast handled by send; the read-only summary reflects server truth
        },
        axios.put,
        true,
        'Save',
        null,
        true
      )
    },

    // XOL-268 Phase B: compose the $confirm body from the applicable change bullets
    buildImpactMessage: function (overrideOn, overrideOff, rateChange, versionChange) {
      var items = []
      if (overrideOn) {
        items.push('<li>This sharer’s fee will be locked at the manual amount and the scheduled rate recalculation will skip this sharer until the override is removed.</li>')
      }
      if (overrideOff) {
        items.push('<li>This sharer’s fee will return to the calculated (automatic) rate and will recompute on its own — this may take a moment to appear.</li>')
      }
      if (rateChange) {
        items.push('<li>This sharer’s fees will be calculated from the new rate’s tiers going forward; past records are not affected.</li>')
      }
      if (versionChange) {
        if (this.selectedVersionId) {
          items.push('<li>This sharer will be pinned to a specific rate version; future fees use that version’s frozen tiers and recompute in the background — this may take a moment.</li>')
        } else {
          items.push('<li>This sharer will follow the current (live) rate again; its fee recomputes in the background.</li>')
        }
      }
      return '<p>Before saving, please note:</p><ul style="margin:0;padding-left:20px;text-align:left;">' + items.join('') + '</ul>'
    },

    // XOL-259: fold the re-pin response into local details so the fee display refreshes
    onVersionRepinned: function (licensee) {
      if (!licensee) return
      if (licensee.calculatedRate !== undefined) {
        this.details.calculatedRate = licensee.calculatedRate
      }
      if (licensee.currentRate !== undefined) {
        this.details.currentRate = licensee.currentRate
      }
      if (licensee.rateVersion !== undefined) {
        this.details.rateVersion = licensee.rateVersion
      }
      if (licensee.updated_at !== undefined) {
        this.details.updated_at = licensee.updated_at
      }
      if (licensee.updated_by !== undefined) {
        this.details.updated_by = licensee.updated_by
      }
    },

    editState: function () {
      if (this.editing) {
        this.details.started = this.formatDateSql(this.details.started)
        this.details.startBilling = this.formatDateSql(this.details.startBilling)
        this.details.expiry = this.formatDateSql(this.details.expiry)
        this.details.terminated = this.formatDateSql(this.details.terminated)

        var overrideChanged = this.details.overrideEnabled !== this.originalOverride
        var rateChanged = Number(this.details.currentRate) !== Number(this.originalRate)
        var rateIdChanged = this.details.rate_id !== this.originalRateId
        var versionIdChanged = this.canSwitchVersion && (this.selectedVersionId !== this.originalVersionId)

        var overrideOn = this.details.overrideEnabled && (overrideChanged || rateChanged)
        var overrideOff = !this.details.overrideEnabled && overrideChanged
        var rateChange = rateIdChanged
        var needsConfirm = overrideOn || overrideOff || rateChange || versionIdChanged

        if (!needsConfirm) {
          this.commitFinance(overrideChanged, rateChanged, rateIdChanged, versionIdChanged)
          return
        }

        var self = this
        this.$confirm(
          this.buildImpactMessage(overrideOn, overrideOff, rateChange, versionIdChanged),
          'Confirm fee change',
          {
            confirmButtonText: 'Save changes',
            cancelButtonText: 'Keep editing',
            center: true,
            dangerouslyUseHTMLString: true,
            type: 'warning'
          }
        ).then(function () {
          self.commitFinance(overrideChanged, rateChanged, rateIdChanged, versionIdChanged)
        }).catch(function () {
          // cancel: write nothing, stay in edit mode
        })
      } else {
        this.editing = true
        this.resumingFee = false
        // XOL-306: seed the version selector when entering edit mode
        if (this.details.rate_id) {
          this.loadVersionsForRate(this.details.rate_id, false)
        }
      }
    }
  },

  watch: {
    details: function (data) {
      this.details = data
    }
  },

  data: function () {
    var derived = this.deriveOverride(this.sharer)
    return {
      menu: 'agreement',
      editing: this.editMode,
      currency: settings.currency,
      details: Object.assign(this.clone(this.sharer), { overrideEnabled: derived }),
      originalRate: this.sharer.currentRate,
      originalOverride: derived,
      originalRateId: this.sharer.rate_id,
      // XOL-364: coerce to Number — the bulk-sharers payload serializes
      // rate_version_id as a string ("4"), but SelectRateVersions' options have
      // numeric ids and el-select matches by strict equality, so a string pin
      // matches no option and the selector renders the raw id instead of the label.
      originalVersionId: (this.sharer.rate_version_id != null && this.sharer.rate_version_id !== '') ? Number(this.sharer.rate_version_id) : null,
      rateVersions: [],
      selectedVersionId: null,
      loadingVersions: false,
      resumingAuto: false,
      resumingFee: false
    }
  }
}

</script>

<style scoped>
/* XOL-367: tighten the two-line "Override/Lock Rate" toggle label. Element's
   .el-form-item__label is line-height:40px, so two lines would be 40px taller
   than the single-line sibling labels — opening a large gap AND dropping the
   checkbox a row below the other inputs. line-height:20px makes the two lines
   occupy 40px (== one sibling line), so the gap tightens and the checkbox
   realigns with the Current Fees / Discount / Interest Rate inputs. */
.override-lock-label ::v-deep .el-form-item__label {
  line-height: 20px;
}
</style>
